/**
 * Get all dynamic labels for a given payoff method
 * @param {number} payoffMethod - The payoff method (0=RULE_OF_78, 1=SIMPLE_INT, 3=SCHEDULE_INT, 4=LEASE, 5=RENT2OWN)
 * @returns {object} - Object with all dynamic labels
 *
 * TERMINOLOGY:
 * - Loan (0,1,3): Principal + Interest
 * - Lease (4): Depreciation + Rent (has Money Factor)
 * - RTO (5): Rent only (no interest, no APR)
 */
export const getPayoffMethodLabels = (payoffMethod) => {
	const isLease = payoffMethod === 4;
	const isRTO = payoffMethod === 5;

	return {
		// Finance/Rent Charge (Interest equivalent)
		financeCharge: isLease ? "Rent Charge" : isRTO ? null : "Finance Charge",

		// Amount Financed / Capitalized Cost
		amountFinanced: isLease
			? "Capitalized Cost"
			: isRTO
			? "Total Rent"
			: "Amount Financed",

		// Interest/Rent terminology - Lease has Rent as interest equivalent, RTO has NO interest
		interestPaid: isLease ? "Rent Paid" : "Interest Paid",
		interestDue: isLease ? "Rent Due" : "Interest Due",
		interestAccrued: isLease ? "Remain Rent" : "Interest Accrued",
		additionalInterest: isLease ? "Additional Rent" : "Additional Interest",

		// APR / Money Factor / Rate - RTO has none
		apr: isLease ? "Money Factor" : "APR (%)",
		hasApr: !isRTO, // RTO doesn't have APR/interest

		// Principal terminology
		// - Loan: Principal
		// - Lease: Depreciation (principal equivalent)
		// - RTO: Rent (the entire payment is rent)
		principal: isLease ? "Depreciation" : isRTO ? "Rent" : "Principal",
		principalPaid: isLease ? "Dep. Paid" : isRTO ? "Rent Paid" : "Prin. Paid",
		principalDue: isLease ? "Dep. Due" : isRTO ? "Rent Due" : "Prin. Due",
		principleRemaining: isLease
			? "Remaining Depreciation"
			: isRTO
			? "Remaining Rent"
			: "Remaining Principal",
		remainPrinciple: isLease
			? "Remain Dep."
			: isRTO
			? "Remain Rent"
			: "Remain Prin.",

		// Balance labels
		princBal: isLease ? "Dep. Bal" : isRTO ? "Rent Bal" : "Prin. Bal",
		loanBal: isLease ? "Lease Bal" : isRTO ? "RTO Bal" : "Loan Bal",

		// Loan terminology
		principleLoan: isLease ? "Depreciation" : isRTO ? "RTO Rent " : "Principal",

		// Payment labels
		regularPayment: isLease
			? "Lease Payment"
			: isRTO
			? "Rent Payment"
			: "Regular Payment",
		downPayment: isLease ? "Drive Off" : "Down Payment",

		// Payoff/Balance labels
		balance: isLease ? "Lease Balance" : isRTO ? "RTO Balance" : "Loan Balance",
		residualValue: isLease ? "Residual Value" : null, // Only Lease has residual
		buyoutAmount: isLease
			? "Buyout Amount"
			: isRTO
			? "Buyout Amount"
			: "Total Payoff",

		// Date section labels
		loanDates: isLease ? "Lease Dates" : isRTO ? "RTO Dates" : "Loan Dates",
		loanDue: isLease ? "Lease Due" : isRTO ? "RTO Due" : "Loan Due",
		loanPaid: isLease ? "Lease Paid" : isRTO ? "RTO Paid" : "Loan Paid",
	};
};
